import { useState, useCallback, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { 
  ArrowLeft, 
  Save, 
  Grid3X3,
  ZoomIn,
  ZoomOut,
  Undo,
  Redo,
  Maximize2,
  Minimize2,
  Check,
  Loader2,
  Magnet
} from 'lucide-react';
import { WhiteboardCanvas } from '../../components/whiteboard/WhiteboardCanvas';
import { WhiteboardToolbar } from '../../components/whiteboard/WhiteboardToolbar';
import { WhiteboardUserStory } from '../../components/whiteboard/WhiteboardUserStory';
import { PropertiesPanel } from '../../components/whiteboard/PropertiesPanel';
import { FlowArrowLayer } from '../../components/whiteboard/FlowArrowLayer';
import { useFrames } from '../../hooks/useFrames';
import { useArrows } from '../../hooks/useArrows';
import type { 
  WhiteboardElement, 
  UserStory, 
  AcceptanceCriterion,
  DeviceType,
  DeviceFrame,
  HistoryEntry,
  SnapGuide,
  AnchorPosition,
} from '../../types/whiteboard';
import { createWhiteboardElement, DEVICE_FRAME_PRESETS } from '../../types/whiteboard';

interface ProjectDetail {
  id: string;
  orgId: string;
  orgName: string;
  name: string;
  key: string;
}

export function WhiteboardPage() {
  const { orgId, projectId } = useParams<{ orgId: string; projectId: string }>();
  
  // ============================================================================
  // State
  // ============================================================================
  const [elements, setElements] = useState<WhiteboardElement[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [userStory, setUserStory] = useState<UserStory>({ asA: '', iWant: '', soThat: '' });
  const [acceptanceCriteria, setAcceptanceCriteria] = useState<AcceptanceCriterion[]>([]);
  
  // Canvas state
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 100, y: 100 });
  const [showGrid, setShowGrid] = useState(true);
  const [gridSize] = useState(20);
  const [snapToGrid, setSnapToGrid] = useState(true);
  const [activeTool, setActiveTool] = useState<string>('select');
  
  // Frame & arrow hooks
  const {
    frames,
    setFrames,
    addFrame,
    updateFrame,
    deleteFrame: deleteFrameHook,
    duplicateFrame,
    changeDevice,
    addChildToFrame,
    removeChildFromFrame,
    findFrameAtPoint,
    autoArrange,
  } = useFrames();

  const {
    arrows,
    setArrows,
    selectedArrowId,
    setSelectedArrowId,
    addArrow,
    updateArrow,
    deleteArrow,
    removeArrowsForEntity,
  } = useArrows();

  // Selection state
  const [selectedFrameId, setSelectedFrameId] = useState<string | null>(null);
  const [focusedFrameId, setFocusedFrameId] = useState<string | null>(null);

  // History for undo/redo
  const [history, setHistory] = useState<HistoryEntry[]>([{ elements: [], frames: [], arrows: [] }]);
  const [historyIndex, setHistoryIndex] = useState(0);
  
  // UI state
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [guides, _setGuides] = useState<SnapGuide[]>([]);
  
  // ============================================================================
  // Queries
  // ============================================================================
  const { data: project, isLoading: projectLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.get<ProjectDetail>(`/projects/${projectId}`),
    enabled: !!projectId,
  });
  
  // ============================================================================
  // Mutations
  // ============================================================================
  const saveStory = useMutation({
    mutationFn: async (data: { 
      userStory: UserStory; 
      elements: WhiteboardElement[];
      acceptanceCriteria: AcceptanceCriterion[];
    }) => {
      return api.post('/ingest/request', {
        projectId,
        title: data.userStory.iWant || 'Whiteboard Story',
        userStory: {
          asA: data.userStory.asA,
          iWant: data.userStory.iWant,
          soThat: data.userStory.soThat,
        },
        selectedElements: data.elements.map(el => ({
          elementId: el.id,
          type: el.type,
          label: el.label,
          meta: {
            type: el.type,
            displayName: el.label,
            state: el.state,
            ...el.props,
          },
          placement: {
            rect: { x: el.x, y: el.y, w: el.width, h: el.height },
            zIndex: el.zIndex,
          },
        })),
        acceptanceCriteria: data.acceptanceCriteria.map(ac => ({
          id: ac.id,
          text: ac.text,
          origin: ac.origin,
        })),
        wireframes: [{
          type: 'whiteboard',
          elements: data.elements,
          frames,
          arrows,
          zoom,
          pan,
        }],
        // Additional frame and flow data for generation
        wireframeFrames: frames.map(f => ({
          name: f.name,
          deviceType: f.deviceType,
          childElements: f.children.length,
        })),
        wireframeFlows: arrows.map(a => ({
          from: getEntityLabel(a.sourceId),
          to: getEntityLabel(a.targetId),
          label: a.label,
        })),
      });
    },
    onMutate: () => {
      setSaveStatus('saving');
    },
    onSuccess: () => {
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    },
    onError: () => {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    },
  });

  /** Helper to get label for element or frame by id */
  const getEntityLabel = useCallback((id: string): string => {
    const el = elements.find(e => e.id === id);
    if (el) return el.label;
    const fr = frames.find(f => f.id === id);
    if (fr) return fr.name;
    return id;
  }, [elements, frames]);
  
  // ============================================================================
  // History Management
  // ============================================================================
  const pushHistory = useCallback((newElements: WhiteboardElement[], newFrames?: DeviceFrame[], newArrows?: any[]) => {
    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push({
        elements: JSON.parse(JSON.stringify(newElements)),
        frames: JSON.parse(JSON.stringify(newFrames ?? frames)),
        arrows: JSON.parse(JSON.stringify(newArrows ?? arrows)),
      });
      if (newHistory.length > 50) newHistory.shift();
      return newHistory;
    });
    setHistoryIndex(prev => Math.min(prev + 1, 49));
  }, [historyIndex, frames, arrows]);
  
  const undo = useCallback(() => {
    if (historyIndex > 0) {
      const entry = history[historyIndex - 1];
      setHistoryIndex(prev => prev - 1);
      setElements(JSON.parse(JSON.stringify(entry.elements)));
      setFrames(JSON.parse(JSON.stringify(entry.frames)));
      setArrows(JSON.parse(JSON.stringify(entry.arrows)));
    }
  }, [historyIndex, history, setFrames, setArrows]);
  
  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const entry = history[historyIndex + 1];
      setHistoryIndex(prev => prev + 1);
      setElements(JSON.parse(JSON.stringify(entry.elements)));
      setFrames(JSON.parse(JSON.stringify(entry.frames)));
      setArrows(JSON.parse(JSON.stringify(entry.arrows)));
    }
  }, [historyIndex, history, setFrames, setArrows]);
  
  // ============================================================================
  // Element Management
  // ============================================================================
  const handleAddElement = useCallback((element: WhiteboardElement) => {
    const newElement = { ...element, zIndex: elements.length + 1 };
    const newElements = [...elements, newElement];
    setElements(newElements);
    
    // Add to frame's children list
    if (newElement.parentFrameId) {
      addChildToFrame(newElement.parentFrameId, newElement.id);
    }
    
    pushHistory(newElements);
    setActiveTool('select');
  }, [elements, pushHistory, addChildToFrame]);
  
  const handleUpdateElement = useCallback((id: string, updates: Partial<WhiteboardElement>) => {
    const newElements = elements.map(el => 
      el.id === id ? { ...el, ...updates } : el
    );
    setElements(newElements);
  }, [elements]);
  
  const handleDeleteElement = useCallback((id: string) => {
    const element = elements.find(el => el.id === id);
    const newElements = elements.filter(el => el.id !== id);
    setElements(newElements);
    
    // Remove from frame children
    if (element?.parentFrameId) {
      removeChildFromFrame(element.parentFrameId, id);
    }
    
    // Remove connected arrows
    removeArrowsForEntity(id);
    
    pushHistory(newElements);
    setSelectedIds(prev => prev.filter(i => i !== id));
  }, [elements, pushHistory, removeChildFromFrame, removeArrowsForEntity]);

  const handleDuplicateElement = useCallback((element: WhiteboardElement) => {
    const newElement = createWhiteboardElement(
      element.type,
      (element.relativeX ?? element.x) + 20,
      (element.relativeY ?? element.y) + 20,
      {
        ...element,
        id: undefined as any,
        label: element.label,
        props: { ...element.props },
        parentFrameId: element.parentFrameId,
        relativeX: element.relativeX !== undefined ? element.relativeX + 20 : undefined,
        relativeY: element.relativeY !== undefined ? element.relativeY + 20 : undefined,
      }
    );
    const newElements = [...elements, newElement];
    setElements(newElements);

    if (newElement.parentFrameId) {
      addChildToFrame(newElement.parentFrameId, newElement.id);
    }

    pushHistory(newElements);
    setSelectedIds([newElement.id]);
  }, [elements, pushHistory, addChildToFrame]);

  // ============================================================================
  // Frame Management
  // ============================================================================
  const handleAddFrame = useCallback((deviceType: DeviceType, x: number, y: number) => {
    const frame = addFrame(deviceType, x, y);
    setSelectedFrameId(frame.id);
    setSelectedIds([]);
    pushHistory(elements);
    setActiveTool('select');
  }, [addFrame, elements, pushHistory]);

  const handleUpdateFrame = useCallback((id: string, updates: Partial<DeviceFrame>) => {
    updateFrame(id, updates);
  }, [updateFrame]);

  const handleDeleteFrame = useCallback((id: string) => {
    // Unparent children
    const newElements = elements.map(el =>
      el.parentFrameId === id
        ? { ...el, parentFrameId: undefined, relativeX: undefined, relativeY: undefined }
        : el
    );
    setElements(newElements);

    deleteFrameHook(id);
    removeArrowsForEntity(id);
    
    if (selectedFrameId === id) setSelectedFrameId(null);
    if (focusedFrameId === id) setFocusedFrameId(null);
    pushHistory(newElements);
  }, [elements, deleteFrameHook, removeArrowsForEntity, selectedFrameId, focusedFrameId, pushHistory]);

  const handleDuplicateFrame = useCallback((frame: DeviceFrame) => {
    const result = duplicateFrame(frame, elements);
    const newElements = [...elements, ...result.childElements];
    setElements(newElements);
    setSelectedFrameId(result.frame.id);
    pushHistory(newElements);
  }, [elements, duplicateFrame, pushHistory]);

  const handleChangeDevice = useCallback((frameId: string, deviceType: DeviceType) => {
    changeDevice(frameId, deviceType);
    pushHistory(elements);
  }, [changeDevice, elements, pushHistory]);

  // ============================================================================
  // Arrow Management
  // ============================================================================
  const handleAddArrow = useCallback((sourceId: string, sourceAnchor: string, targetId: string, targetAnchor: string) => {
    addArrow(sourceId, sourceAnchor as AnchorPosition, targetId, targetAnchor as AnchorPosition);
    pushHistory(elements);
    setActiveTool('select');
  }, [addArrow, elements, pushHistory]);

  const handleDeleteArrow = useCallback((id: string) => {
    deleteArrow(id);
    pushHistory(elements);
  }, [deleteArrow, elements, pushHistory]);

  // ============================================================================
  // Zoom Controls
  // ============================================================================
  const handleZoomIn = useCallback(() => {
    setZoom(z => Math.min(3, z + 0.25));
  }, []);
  
  const handleZoomOut = useCallback(() => {
    setZoom(z => Math.max(0.25, z - 0.25));
  }, []);
  
  const handleZoomReset = useCallback(() => {
    setZoom(1);
    setPan({ x: 100, y: 100 });
  }, []);
  
  const handleZoomFit = useCallback(() => {
    if (elements.length === 0 && frames.length === 0) {
      handleZoomReset();
      return;
    }
    
    // Calculate bounding box including frames
    const allRects = [
      ...elements.filter(el => !el.parentFrameId).map(el => ({
        x: el.x, y: el.y, right: el.x + el.width, bottom: el.y + el.height,
      })),
      ...frames.map(f => {
        const preset = f.deviceType !== 'custom' ? DEVICE_FRAME_PRESETS[f.deviceType] : null;
        const chromeH = 36 + ((preset?.chromeType === 'phone') ? 44 : 0);
        return {
          x: f.x, y: f.y, right: f.x + f.viewportWidth, bottom: f.y + f.viewportHeight + chromeH,
        };
      }),
    ];

    if (allRects.length === 0) {
      handleZoomReset();
      return;
    }
    
    const minX = Math.min(...allRects.map(r => r.x));
    const minY = Math.min(...allRects.map(r => r.y));
    const maxX = Math.max(...allRects.map(r => r.right));
    const maxY = Math.max(...allRects.map(r => r.bottom));
    
    const contentWidth = maxX - minX + 100;
    const contentHeight = maxY - minY + 100;
    
    const viewportWidth = window.innerWidth - 400;
    const viewportHeight = window.innerHeight - 60;
    
    const scaleX = viewportWidth / contentWidth;
    const scaleY = viewportHeight / contentHeight;
    const newZoom = Math.min(scaleX, scaleY, 1);
    
    setZoom(newZoom);
    setPan({
      x: (viewportWidth - contentWidth * newZoom) / 2 - minX * newZoom + 50,
      y: (viewportHeight - contentHeight * newZoom) / 2 - minY * newZoom + 50,
    });
  }, [elements, frames, handleZoomReset]);
  
  // ============================================================================
  // Keyboard Shortcuts
  // ============================================================================
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).tagName === 'INPUT' || 
          (e.target as HTMLElement).tagName === 'TEXTAREA') {
        return;
      }
      
      // Undo/Redo
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && e.shiftKey) {
        e.preventDefault();
        redo();
      }
      
      // Save
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        saveStory.mutate({ userStory, elements, acceptanceCriteria });
      }
      
      // Tool shortcuts
      if (e.key === 'v') setActiveTool('select');
      if (e.key === 'h') setActiveTool('pan');
      if (e.key === 'b') setActiveTool('button');
      if (e.key === 'i') setActiveTool('input');
      if (e.key === 'd') setActiveTool('dropdown');
      if (e.key === 't') setActiveTool('table');
      if (e.key === 'x') setActiveTool('text');
      if (e.key === 'r') setActiveTool('shape_rect');
      if (e.key === 'o') setActiveTool('shape_circle');
      if (e.key === 'f') setActiveTool('frame:iphone_15');
      if (e.key === 'a' && !(e.metaKey || e.ctrlKey)) setActiveTool('arrow');

      // Enter frame
      if (e.key === 'Enter' && selectedFrameId) {
        e.preventDefault();
        setFocusedFrameId(selectedFrameId);
      }

      // Escape: exit frame or deselect
      if (e.key === 'Escape') {
        if (focusedFrameId) {
          setFocusedFrameId(null);
        }
      }

      // Delete selected arrow
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedArrowId) {
        e.preventDefault();
        handleDeleteArrow(selectedArrowId);
      }

      // Delete selected frame
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedFrameId && selectedIds.length === 0) {
        e.preventDefault();
        handleDeleteFrame(selectedFrameId);
      }
      
      // Zoom shortcuts
      if ((e.metaKey || e.ctrlKey) && e.key === '=') {
        e.preventDefault();
        handleZoomIn();
      }
      if ((e.metaKey || e.ctrlKey) && e.key === '-') {
        e.preventDefault();
        handleZoomOut();
      }
      if ((e.metaKey || e.ctrlKey) && e.key === '0') {
        e.preventDefault();
        handleZoomReset();
      }
      if ((e.metaKey || e.ctrlKey) && e.key === '1') {
        e.preventDefault();
        handleZoomFit();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo, handleZoomIn, handleZoomOut, handleZoomReset, handleZoomFit, userStory, elements, acceptanceCriteria, saveStory, selectedFrameId, focusedFrameId, selectedArrowId, handleDeleteArrow, handleDeleteFrame, selectedIds.length]);
  
  // Wheel zoom
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        setZoom(z => Math.min(3, Math.max(0.25, z + delta)));
      }
    };
    window.addEventListener('wheel', handleWheel, { passive: false });
    return () => window.removeEventListener('wheel', handleWheel);
  }, []);
  
  // ============================================================================
  // Fullscreen
  // ============================================================================
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);
  
  // ============================================================================
  // Save
  // ============================================================================
  const handleSave = useCallback(() => {
    saveStory.mutate({ userStory, elements, acceptanceCriteria });
  }, [saveStory, userStory, elements, acceptanceCriteria]);

  // ============================================================================
  // Properties Panel -- determine what to show
  // ============================================================================
  const selectedElement = selectedIds.length === 1
    ? elements.find(el => el.id === selectedIds[0]) ?? null
    : null;

  const selectedFrame = selectedFrameId
    ? frames.find(f => f.id === selectedFrameId) ?? null
    : null;

  const showPropertiesPanel = selectedElement !== null || selectedFrame !== null || selectedArrowId !== null;

  const selectedArrow = selectedArrowId
    ? arrows.find(a => a.id === selectedArrowId) ?? null
    : null;
  
  // ============================================================================
  // Render
  // ============================================================================
  if (projectLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-main">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  return (
    <div className="h-screen flex flex-col bg-main overflow-hidden">
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-2 bg-surface border-b border-default shrink-0">
        <div className="flex items-center gap-3">
          <Link 
            to={`/app/organizations/${orgId}/projects/${projectId}`} 
            className="btn-icon"
            title="Back to project"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          
          <div className="h-6 w-px bg-border" />
          
          <div>
            <h1 className="text-base font-semibold text-default flex items-center gap-2">
              Wireframe Editor
              <span className="px-2 py-0.5 text-[10px] font-medium bg-primary-dim text-primary rounded-full">
                Beta
              </span>
            </h1>
            <p className="text-xs text-muted">{project?.name}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Undo/Redo */}
          <div className="flex items-center gap-0.5 mr-2">
            <button 
              onClick={undo}
              disabled={historyIndex === 0}
              className="btn-icon disabled:opacity-30"
              title="Undo (Cmd+Z)"
            >
              <Undo className="w-4 h-4" />
            </button>
            <button 
              onClick={redo}
              disabled={historyIndex >= history.length - 1}
              className="btn-icon disabled:opacity-30"
              title="Redo (Cmd+Shift+Z)"
            >
              <Redo className="w-4 h-4" />
            </button>
          </div>
          
          <div className="h-6 w-px bg-border" />
          
          {/* Zoom controls */}
          <div className="flex items-center gap-1 px-2 py-1 bg-surface-2 rounded-lg">
            <button 
              onClick={handleZoomOut} 
              className="p-1 rounded hover:bg-surface-3 text-muted hover:text-default"
              title="Zoom out (Cmd+-)"
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <button 
              onClick={handleZoomReset}
              className="text-sm font-medium min-w-[4rem] text-center hover:bg-surface-3 rounded px-2 py-0.5"
              title="Reset zoom (Cmd+0)"
            >
              {Math.round(zoom * 100)}%
            </button>
            <button 
              onClick={handleZoomIn} 
              className="p-1 rounded hover:bg-surface-3 text-muted hover:text-default"
              title="Zoom in (Cmd++)"
            >
              <ZoomIn className="w-4 h-4" />
            </button>
          </div>
          
          <button 
            onClick={handleZoomFit}
            className="btn-icon"
            title="Fit to content (Cmd+1)"
          >
            <Maximize2 className="w-4 h-4" />
          </button>
          
          <div className="h-6 w-px bg-border" />
          
          {/* Grid toggle */}
          <button 
            onClick={() => setShowGrid(!showGrid)} 
            className={`btn-icon ${showGrid ? 'bg-primary-dim text-primary' : ''}`}
            title="Toggle grid"
          >
            <Grid3X3 className="w-4 h-4" />
          </button>
          
          {/* Snap toggle */}
          <button 
            onClick={() => setSnapToGrid(!snapToGrid)} 
            className={`btn-icon ${snapToGrid ? 'bg-primary-dim text-primary' : ''}`}
            title="Snap to grid"
          >
            <Magnet className="w-4 h-4" />
          </button>
          
          {/* Fullscreen */}
          <button 
            onClick={toggleFullscreen}
            className="btn-icon"
            title="Toggle fullscreen"
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
          
          <div className="h-6 w-px bg-border" />
          
          {/* Save button */}
          <button 
            onClick={handleSave}
            disabled={saveStory.isPending || !userStory.iWant}
            className={`btn px-4 py-2 ${
              saveStatus === 'saved' 
                ? 'btn-success' 
                : saveStatus === 'error'
                ? 'btn-danger'
                : 'btn-primary'
            }`}
          >
            {saveStatus === 'saving' ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : saveStatus === 'saved' ? (
              <>
                <Check className="w-4 h-4" />
                Saved!
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Story
              </>
            )}
          </button>
        </div>
      </header>
      
      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left toolbar */}
        <WhiteboardToolbar 
          activeTool={activeTool}
          onToolChange={setActiveTool}
        />
        
        {/* Canvas + Arrow overlay */}
        <div className="flex-1 relative overflow-hidden">
          <WhiteboardCanvas
            elements={elements}
            frames={frames}
            arrows={arrows}
            selectedIds={selectedIds}
            selectedFrameId={selectedFrameId}
            focusedFrameId={focusedFrameId}
            zoom={zoom}
            pan={pan}
            showGrid={showGrid}
            gridSize={gridSize}
            snapToGrid={snapToGrid}
            activeTool={activeTool}
            guides={guides}
            onSelect={setSelectedIds}
            onSelectFrame={setSelectedFrameId}
            onUpdate={handleUpdateElement}
            onDelete={handleDeleteElement}
            onAddElement={handleAddElement}
            onPanChange={setPan}
            onAddFrame={handleAddFrame}
            onUpdateFrame={handleUpdateFrame}
            onDeleteFrame={handleDeleteFrame}
            onDuplicateFrame={handleDuplicateFrame}
            onChangeDevice={handleChangeDevice}
            onAddArrow={handleAddArrow}
            onSelectArrow={setSelectedArrowId}
            selectedArrowId={selectedArrowId}
            findFrameAtPoint={findFrameAtPoint}
            onElementDuplicate={handleDuplicateElement}
          />

          {/* Flow Arrow SVG overlay */}
          <FlowArrowLayer
            arrows={arrows}
            elements={elements}
            frames={frames}
            selectedArrowId={selectedArrowId}
            zoom={zoom}
            pan={pan}
            onSelectArrow={setSelectedArrowId}
            onUpdateArrow={updateArrow}
            onDeleteArrow={handleDeleteArrow}
          />
          
          {/* Mini info bar */}
          <div className="absolute bottom-4 left-4 flex items-center gap-3 px-3 py-2 bg-surface/90 backdrop-blur-sm rounded-lg border border-default shadow-sm text-xs text-muted">
            <span>{elements.length} elements</span>
            <span className="w-px h-4 bg-border" />
            <span>{frames.length} frames</span>
            <span className="w-px h-4 bg-border" />
            <span>{arrows.length} arrows</span>
            <span className="w-px h-4 bg-border" />
            <span>{selectedIds.length} selected</span>
          </div>
        </div>
        
        {/* Right panel - Properties or User Story */}
        {showPropertiesPanel ? (
          <PropertiesPanel
            element={selectedElement}
            frame={selectedFrame}
            arrow={selectedArrow}
            frames={frames}
            onUpdateElement={(updates) => {
              if (selectedElement) handleUpdateElement(selectedElement.id, updates);
            }}
            onUpdateFrame={(updates) => {
              if (selectedFrame) handleUpdateFrame(selectedFrame.id, updates);
            }}
            onUpdateArrow={(updates) => {
              if (selectedArrow) updateArrow(selectedArrow.id, updates);
            }}
            onAutoArrange={() => {
              if (selectedFrame) {
                const updates = autoArrange(selectedFrame.id, elements);
                for (const upd of updates) {
                  const id = (upd as any).id;
                  if (id) handleUpdateElement(id, upd);
                }
                pushHistory(elements);
              }
            }}
            onClose={() => {
              setSelectedIds([]);
              setSelectedFrameId(null);
              setSelectedArrowId(null);
            }}
          />
        ) : (
          <WhiteboardUserStory
            projectId={projectId || ''}
            userStory={userStory}
            elements={elements}
            acceptanceCriteria={acceptanceCriteria}
            onUserStoryChange={setUserStory}
            onAcceptanceCriteriaChange={setAcceptanceCriteria}
          />
        )}
      </div>
    </div>
  );
}
